import { useState, useEffect } from "react";
import { storageUrl } from "@/services/config";
import api from "@/services/api";
import type { ApiInstagramImage } from "@/types/api";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, FreeMode } from "swiper/modules";

import "swiper/css";

const INSTAGRAM_URL = "https://www.instagram.com/souad_a_haj";

const fallbackImages = [
  "https://readdy.ai/api/search-image?query=Flatlay%20of%20luxury%20skincare%20products%20on%20marble%20surface%20with%20rose%20petals%20soft%20natural%20light%20warm%20beige%20background%20editorial%20beauty%20photography&width=800&height=800&seq=7001&orientation=squarish",

  "https://readdy.ai/api/search-image?query=Woman%20with%20glowing%20skin%20applying%20serum%20in%20soft%20natural%20light%20minimal%20elegant%20skincare%20routine%20warm%20beige%20background&width=800&height=800&seq=7002&orientation=squarish",

  "https://readdy.ai/api/search-image?query=Luxury%20makeup%20brushes%20and%20lipstick%20arrangement%20on%20vanity%20table%20soft%20warm%20light%20minimal%20aesthetic%20beauty%20photography&width=800&height=800&seq=7003&orientation=squarish",

  "https://readdy.ai/api/search-image?query=Elegant%20woman%20portrait%20with%20minimal%20makeup%20natural%20beauty%20soft%20light%20warm%20beige%20background%20beauty%20editorial%20photography&width=800&height=800&seq=7004&orientation=squarish",

  "https://readdy.ai/api/search-image?query=Body%20care%20products%20oil%20bottle%20and%20bath%20salts%20on%20marble%20surface%20with%20dried%20flowers%20soft%20warm%20light%20minimal%20aesthetic&width=800&height=800&seq=7005&orientation=squarish",

  "https://readdy.ai/api/search-image?query=Beauty%20gift%20set%20wrapped%20in%20silk%20ribbon%20on%20soft%20velvet%20surface%20with%20pearls%20elegant%20luxury%20photography&width=800&height=800&seq=7006&orientation=squarish",
];

export default function InstagramSection() {
  const [images, setImages] = useState<ApiInstagramImage[]>([]);

  useEffect(() => {
    api
      .get<ApiInstagramImage[]>("/instagram-images")
      .then((res) => {
        setImages(res.data);
      })
      .catch(() => {});
  }, []);

  const gridImages =
    images.length > 0
      ? images.map((img) => ({
          url: storageUrl(img.image_path),
          link: img.link || INSTAGRAM_URL,
          caption: img.caption || "",
        }))
      : fallbackImages.map((url) => ({
          url,
          link: INSTAGRAM_URL,
          caption: "",
        }));

  return (
    <section className="w-full bg-white m-0 p-0 overflow-hidden">
      {/* Header */}
      <div className="text-center pt-8 pb-6 md:pt-10 md:pb-8">
        <h2 className="heading-lg">تابعينا على إنستغرام</h2>

        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="
            inline-block
            mt-2
            text-sm
            font-medium
            tracking-wide
            text-gray-800
            hover:opacity-60
            transition-opacity
            underline
            underline-offset-4
          "
        >
          FOLLOW US
        </a>
      </div>

      {/* Slider */}
      <div className="w-full">
        <Swiper
          modules={[Autoplay, FreeMode]}
          loop={true}
          freeMode={false}
          grabCursor={true}
          watchSlidesProgress={true}
          speed={700}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          slidesPerView={1.4}
          breakpoints={{
            640: {
              slidesPerView: 2.5,
            },

            768: {
              slidesPerView: 3.5,
            },

            1024: {
              slidesPerView: 5,
            },

            1280: {
              slidesPerView: 6,
            },
          }}
          className="instagram-swiper"
        >
          {gridImages.map((img, i) => (
            <SwiperSlide key={i}>
              <a
                href={img.link}
                target="_blank"
                rel="noopener noreferrer"
                className="
                  relative
                  block
                  w-full
                  aspect-square
                  overflow-hidden
                  group
                "
              >
                <img
                  src={img.url}
                  alt={img.caption || `Instagram ${i + 1}`}
                  className="
                    w-full
                    h-full
                    object-cover
                    transition-transform
                    duration-500
                    group-hover:scale-105
                  "
                  loading="lazy"
                />

                {/* Hover */}
                <div
                  className="
                    absolute
                    inset-0
                    flex
                    items-center
                    justify-center
                    bg-black/0
                    group-hover:bg-black/30
                    transition-all
                    duration-300
                  "
                >
                  <i
                    className="
                      ri-instagram-line
                      text-3xl
                      text-white
                      opacity-0
                      group-hover:opacity-100
                      transition-opacity
                      duration-300
                    "
                  />
                </div>
              </a>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
}
